import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { FocusSession, CreateFocusSessionDTO, UpdateFocusSessionDTO } from '../../components/page-components/Dashboard/types';
import api from '../../api';

// Focus Session state interface
interface FocusSessionState {
  sessions: FocusSession[];
  currentSession: FocusSession | null;
  isLoading: boolean;
  error: string | null;
}

// Initial state
const initialState: FocusSessionState = {
  sessions: [],
  currentSession: null,
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchAllFocusSessions = createAsyncThunk(
  'focusSession/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const sessions = await api.focus.getAll();
      return sessions;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch focus sessions'
      );
    }
  }
);

export const fetchFocusSessionById = createAsyncThunk(
  'focusSession/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const session = await api.focus.getById(id);
      return session;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to fetch focus session'
      );
    }
  }
);

export const createFocusSession = createAsyncThunk(
  'focusSession/create',
  async (data: CreateFocusSessionDTO, { rejectWithValue }) => {
    try {
      const session = await api.focus.create(data);
      return session;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to create focus session'
      );
    }
  }
);

export const updateFocusSession = createAsyncThunk(
  'focusSession/update',
  async ({ id, data }: { id: string; data: UpdateFocusSessionDTO }, { rejectWithValue }) => {
    try {
      const session = await api.focus.update(id, data);
      return session;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to update focus session'
      );
    }
  }
);

export const deleteFocusSession = createAsyncThunk(
  'focusSession/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await api.focus.delete(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || error.message || 'Failed to delete focus session'
      );
    }
  }
);

// Focus Session slice
const focusSessionSlice = createSlice({
  name: 'focusSession',
  initialState,
  reducers: {
    setCurrentSession: (state, action: PayloadAction<FocusSession | null>) => {
      state.currentSession = action.payload;
    },
    clearCurrentSession: (state) => {
      state.currentSession = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all focus sessions
    builder
      .addCase(fetchAllFocusSessions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllFocusSessions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.sessions = action.payload;
      })
      .addCase(fetchAllFocusSessions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Fetch focus session by ID
    builder
      .addCase(fetchFocusSessionById.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchFocusSessionById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentSession = action.payload;
        // Update the session in the sessions array if it exists
        const index = state.sessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
      })
      .addCase(fetchFocusSessionById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Create focus session
    builder
      .addCase(createFocusSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createFocusSession.fulfilled, (state, action) => {
        state.isLoading = false;
        // Only add to store on successful creation
        state.sessions.unshift(action.payload); // Add to beginning of array
        state.currentSession = action.payload;
      })
      .addCase(createFocusSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Update focus session
    builder
      .addCase(updateFocusSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateFocusSession.fulfilled, (state, action) => {
        state.isLoading = false;
        // Update session in sessions array
        const index = state.sessions.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.sessions[index] = action.payload;
        }
        // Update current session if it's the one being updated
        if (state.currentSession?.id === action.payload.id) {
          state.currentSession = action.payload;
        }
      })
      .addCase(updateFocusSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });

    // Delete focus session
    builder
      .addCase(deleteFocusSession.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(deleteFocusSession.fulfilled, (state, action) => {
        state.isLoading = false;
        // Remove session from sessions array
        state.sessions = state.sessions.filter(s => s.id !== action.payload);
        // Clear current session if it was deleted
        if (state.currentSession?.id === action.payload) {
          state.currentSession = null;
        }
      })
      .addCase(deleteFocusSession.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentSession, clearCurrentSession, setError, clearError } = focusSessionSlice.actions;
export default focusSessionSlice.reducer;

